import { EXAMINER_ROLE_KEY } from './components/RoleAccess'

export const TEACHER_IMPERSONATION_KEY = 'ukbc_teacher_impersonation'

export interface TeacherImpersonationInfo {
    examinerUsername: string
    teacherId: string
    teacherName: string
    teacherSchoolCode: string
}

export function getTeacherImpersonation(): TeacherImpersonationInfo | null {
    const raw = sessionStorage.getItem(TEACHER_IMPERSONATION_KEY)
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw)
        if (
          typeof parsed.examinerUsername === 'string' &&
          typeof parsed.teacherId === 'string' &&
          typeof parsed.teacherName === 'string' &&
          typeof parsed.teacherSchoolCode === 'string'
        ) {
            return parsed
        }
    } catch (_) {
        return null
    }

    return null
}

export function setTeacherImpersonation(info: TeacherImpersonationInfo | null) {
    if (!info) {
        sessionStorage.removeItem(TEACHER_IMPERSONATION_KEY)
        return
    }
    sessionStorage.setItem(TEACHER_IMPERSONATION_KEY, JSON.stringify(info))
}

export function clearTeacherImpersonation() {
    sessionStorage.removeItem(TEACHER_IMPERSONATION_KEY)
}

let config:any = null

export function getConfigSync() {

    if(!config) {
        throw new Error('config not yet loaded')
    }

    return config
}

export async function getConfig() {

    if(config)
        return config

    let frontendConfig = await (await fetch('/config.json')).json()
    let backendConfig = await (await fetch(frontendConfig.apiUrl + '/api/config')).json()

    config = {
        ...frontendConfig,
        ...backendConfig
    }

    return config
}

export default async function apiRequest(input:RequestInfo, init?:RequestInit|undefined):Promise<Response> {

    let reqType = input.toString().split('/')[2]

    let token = sessionStorage.getItem(`ukbc_${reqType}`)

    init = init || {}
    init.headers = init.headers || {};

    (init.headers as any)['x-api-key'] = token

    let res = await fetch((await getConfig()).apiUrl + input, init)

    // if (res.status === 403) {
    //     window.location.href = '/' + reqType + '/login'
    // }

    return res
}

export async function apiVerifyTeacherEmail(token: string): Promise<{ ok: boolean; reason?: string }> {
    const res = await fetch((await getConfig()).apiUrl + `/api/public/teacher/verify-email/${encodeURIComponent(token)}`, {
        method: 'POST',
    })

    if (res.ok) {
        return { ok: true }
    }

    try {
        const data = await res.json()
        return { ok: false, reason: data.reason }
    } catch {
        return { ok: false, reason: 'error' }
    }
}

export async function apiLogin(username:string, password:string, recaptchaToken?:string):Promise<{type:string|null,error:string|null}> {

    let res = await fetch((await getConfig()).apiUrl + `/api/login`, {
        method: 'POST',
        body: JSON.stringify({
            username,
            password,
            ...(recaptchaToken && { recaptchaToken })
        }),
        headers: {
            'content-type': 'application/json'
        }
    })

    if(res.status !== 200) {
        return { error: await res.text(), type: null }
    }

    let { token, type, examinerRole } = await res.json()

    sessionStorage.setItem(`ukbc_${type}`, token)

    if (type === 'examiner') {
        sessionStorage.setItem(
          EXAMINER_ROLE_KEY,
          typeof examinerRole === 'string' ? examinerRole : 'examiner'
        )
    }

    return { error: null, type }
}

export async function apiResetPassword(username:string):Promise<{success?:string, error?:string}> {

    let res = await fetch((await getConfig()).apiUrl + `/api/resetPassword`, {
        method: 'POST',
        body: JSON.stringify({
            username
        }),
        headers: {
            'content-type': 'application/json'
        }
    })

    let message = await res.text()

    if (res.ok) {
        return { success: message }
    }

    return { error: message }

}

export function isLoggedIn(userType:string):boolean {

    let token = sessionStorage.getItem(`ukbc_${userType}`)

    return !!token
}

export async function apiLoginAsTeacher(teacherId: string): Promise<{ error: string | null }> {
    const res = await apiRequest(`/api/examiner/teachers/${teacherId}/loginAs`, {
        method: 'POST',
    })

    if (!res.ok) {
        return { error: await res.text() || 'Failed to login as teacher' }
    }

    const body = await res.json()
    sessionStorage.setItem(`ukbc_${body.type}`, body.token)
    if (body.impersonation) {
        setTeacherImpersonation(body.impersonation)
    }
    return { error: null }
}
